/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : Integration.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import React, { useEffect, useState, useCallback } from "react";
import JiraIcon from "../../assets/Jira.png";
import XrayIcon from "../../assets/PikPng.com_xray-png_5613333.png";
import ServiceNowIcon from "../../assets/ServiceNow_idec6JyuYi_1.png";
import GitPng from "../../assets/gitPng.png";
import AddIntegrations from "./AddIntegrations";
import EditIntegrations from "./EditIntegrations";
import { useDispatch } from "react-redux";
import { HandleApiActions } from "../../redux/actions";
import integrationConfigAction from "../../redux/actions/integrationConfigAction";
import CommonBreadcrumbs from "../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import CustomSearchbarWithApiV2 from "../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import CustomFilterV2 from "../../components/customComponentsV2/common/filter/CustomFilterV2";
import CustomCheckBoxV2 from "../../components/customComponentsV2/common/checkBox/CustomCheckBoxV2";
import CustomRadioButtonV2 from "../../components/customComponentsV2/common/radioButton/CustomRadioButtonV2";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import sortUp from "../../assets/sortup.png";
import sortDown from "../../assets/sortdown.png";
import AddGitIntegrations from "./AddGitToSoftwareModules";
import PTC from "../../assets/PTC.png";

function Integration() {
  const dispatch = useDispatch();

  const toSentenceCase = (text) => {
    if (!text) return "-";
    return text
      .toLowerCase()
      .split(" ")
      .map((word, index) => {
        if (index === 0) {
          return word.charAt(0).toUpperCase() + word.slice(1);
        }
        return word;
      })
      .join(" ");
  };
  const toTitleCase = (text) => {
    if (!text) return "-";
    return text
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const [openAddIntegrations, setOpenAddIntegrations] = useState(false);
  const [openEditIntegrations, setOpenEditIntegrations] = useState(false);
  const [openDeleteIntegrations, setOpenDeleteIntegrations] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [getAllConnectors, setGetAllConnectors] = useState([]);
  const [getAllIntegrations, setGetAllIntegrations] = useState([]);

  const [anchorElIntegrationFilter, setAnchorElIntegrationFilter] =
    useState(null);
  const [openIntegrationFilter, setOpenIntegrationFilter] = useState(false);

  const [anchorElMenu, setAnchorElMenu] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const [openAddGitIntegrations, setOpenAddGitIntegrations] = useState(false);
  const [filters, setFilters] = useState({
    sortBy: "Created at",
    sortOrder: "Sort Asc –Des",
    connectionStatus: {
      all: true,
      connected: true,
      disconnected: true,
    },
    toolTypes: {
      all: true,
      jiraXray: true,
      jiraSoftware: true,
      snow: true,
      github: true,
      codeBeamer: true,
    },
    pages: {
      all: true,
      testCases: true,
      virtualLabCar: true,
      legalRequirement: true,
      projectConfigurator: true,
    },
  });

  const integrationsList = Array.isArray(getAllIntegrations?.data)
    ? getAllIntegrations.data
    : [];

  const handleIntegrationFilterIconClick = (event) => {
    setAnchorElIntegrationFilter(event.currentTarget);
    setOpenIntegrationFilter(true);
  };

  const handleIntegrationFilterIconClose = () => {
    setAnchorElIntegrationFilter(null);
    setOpenIntegrationFilter(false);
  };

  const handleIntegrationFilterButton = (action) => {
    if (action === "apply") {
      applyFilters();
    } else if (action === "cancel") {
      handleIntegrationFilterIconClose();
    } else if (action === "discard") {
      resetFilters();
    }
  };

  const handleMoreVertClick = (event, item) => {
    setAnchorElMenu(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorElMenu(null);
    setSelectedItem(null);
  };

  const handleEditClick = () => {
    if (selectedItem) {
      setOpenEditIntegrations(true);
      setAnchorElMenu(null);
    }
  };

  const handleDeleteMenuClick = () => {
    if (selectedItem) {
      setSelectedId(selectedItem.id);
      setOpenDeleteIntegrations(true);
    }
    handleMenuClose();
  };

  const getIconColor = (toolName) => {
    const colors = {
      JIRA: "#ffffff",
      "JIRA Software": "#ffffff",
      "JIRA Xray": "#ffffff",
      "JIRA Xray Test Case Management": "#ffffff",
      ServiceNow: "#ffffff",
    };

    let color = colors[toolName];

    if (!color && toolName) {
      const lowerToolName = toolName.toLowerCase();
      if (
        lowerToolName.includes("servicenow") ||
        lowerToolName.includes("snow") ||
        (lowerToolName.includes("service") && lowerToolName.includes("now"))
      ) {
        color = "#00c853";
      }
    }

    return color;
  };

  const getToolIcon = (toolName) => {
    console.log("getToolIcon called with:", toolName);
    const iconMap = {
      JIRA: JiraIcon,
      "JIRA Software": JiraIcon,
      "JIRA Xray": XrayIcon,
      "JIRA Xray Test Case Management": XrayIcon,
      GitHub: GitPng,
      ServiceNow: ServiceNowIcon,
      codeBeamer: PTC,
      CodeBeamer: PTC,
    };

    let result = iconMap[toolName];

    if (!result && toolName) {
      const lowerToolName = toolName.toLowerCase();
      if (lowerToolName.includes("jira") || lowerToolName.includes("xray")) {
        result = lowerToolName.includes("xray") ? XrayIcon : JiraIcon;
      } else if (lowerToolName.includes("codebeamer")) {
        result = PTC;
      } else if (
        lowerToolName.includes("service") &&
        lowerToolName.includes("now")
      ) {
        result = ServiceNowIcon;
      } else if (
        lowerToolName.includes("servicenow") ||
        lowerToolName.includes("snow")
      ) {
        result = ServiceNowIcon;
      }
    }

    console.log("getToolIcon returning:", result);
    return result || null;
  };

  const resetFilters = () => {
    setFilters({
      sortBy: "Created at",
      sortOrder: "Sort Asc –Des",
      connectionStatus: {
        all: true,
        connected: true,
        disconnected: true,
      },
      toolTypes: {
        all: true,
        jiraXray: true,
        jiraSoftware: true,
        snow: true,
        github: true,
        codeBeamer: true,
      },
      pages: {
        all: true,
        testCases: true,
        virtualLabCar: true,
        legalRequirement: true,
        projectConfigurator: true,
      },
    });
    handleIntegrationFilterIconClose(); // Close CustomFilterV2
    fetchIntegrationDetails();
  };

  const applyFilters = async () => {
    handleIntegrationFilterIconClose(); // Close CustomFilterV2

    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...integrationConfigAction.view_integrations,
          params: {
            is_deleted: false,
          },
        }),
      ),
    );

    if (resp?.error) {
      setGetAllIntegrations({ data: [] });
      return;
    }

    if (resp?.data) {
      let filteredData = [...resp.data];






      const matchesConnectionStatus = (item, filters) => {
        if (filters.connectionStatus.all) return true;

        const isConnected = item.is_connected;

        return (
          (filters.connectionStatus.connected && isConnected) ||
          (filters.connectionStatus.disconnected && !isConnected)
        );
      };
      const matchesPages = (item, filters) => {
        if (filters.pages.all) return true;

        const moduleType = item.module_type;

        return (
          (filters.pages.testCases && moduleType === "TEST CASES") ||
          (filters.pages.virtualLabCar && moduleType === "VIRTUAL LAB-CAR") ||
          (filters.pages.legalRequirement &&
            moduleType === "LEGAL REQUIREMENTS") ||
          (filters.pages.projectConfigurator &&
            moduleType === "PROJECT CONFIGURATOR")
        );
      };
      const matchesToolTypes = (item, filters) => {
        if (filters.toolTypes.all) return true;

        const connectorType = item.connector_type?.toUpperCase() || "";
        const integrateSelect = getSelectedConnectors(connectorType);
        const toolName = integrateSelect?.name?.toLowerCase() || "";

        return (
          (filters.toolTypes.jiraXray &&
            (toolName.includes("xray") || connectorType.includes("XRAY"))) ||
          (filters.toolTypes.jiraSoftware &&
            ((toolName.includes("jira") && !toolName.includes("xray")) ||
              (connectorType.includes("JIRA") &&
                !connectorType.includes("XRAY")))) ||
          (filters.toolTypes.snow &&
            (toolName.includes("servicenow") ||
              toolName.includes("service now") ||
              toolName.includes("snow") ||
              connectorType.includes("SERVICE") ||
              connectorType.includes("SNOW"))) ||
          (filters.toolTypes.github &&
            (toolName.includes("github") ||
              connectorType.includes("GITHUB"))) ||
          (filters.toolTypes.codeBeamer &&
            (toolName.includes("codebeamer") ||
              connectorType.includes("CODEBEAMER")))
        );
      };
      filteredData = filteredData.filter((item) => {
        if (!matchesConnectionStatus(item, filters)) return false;

        if (!matchesPages(item, filters)) return false;

        if (!matchesToolTypes(item, filters)) return false;

        return true;
      });

      if (filters.sortBy) {
        filteredData.sort((a, b) => {
          let aValue, bValue;

          if (filters.sortBy === "Created at") {
            aValue = new Date(a.created_at || 0);
            bValue = new Date(b.created_at || 0);
          } else if (filters.sortBy === "Updated at") {
            aValue = new Date(a.updated_at || 0);
            bValue = new Date(b.updated_at || 0);
          }

          if (filters.sortOrder === "Sort Asc –Des") {
            return aValue - bValue;
          } else {
            return bValue - aValue;
          }
        });
      }

      setGetAllIntegrations({ data: filteredData });
      return;
    }

    setGetAllIntegrations({ data: [] });
  };

  const handleConfirmDelete = async () => {
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...integrationConfigAction.update_integrations,
          params: {
            id: Number.parseInt(selectedId, 10),
            status: true,
          },
          show_toast: true,
        }),
      ),
    );

    if (!resp?.error) {
      fetchIntegrationDetails();
      setOpenDeleteIntegrations(false);
      setGetAllIntegrations({ data: [] });
    }
  };

  useEffect(() => {
    const fetchConnectorDetails = async () => {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...integrationConfigAction.get_connectors,
          }),
        ),
      );
      console.log("---Get Connectors---", resp);
      if (!resp?.error) {
        setGetAllConnectors(resp?.data);
      }
    };
    fetchConnectorDetails();
  }, [dispatch]);
  const fetchIntegrationDetails = useCallback(async () => {
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...integrationConfigAction.view_integrations,
          params: {
            is_deleted: false,
          },
        }),
      ),
    );
    console.log("resskfdposkfpofpds fpdsf", resp);

    if (resp?.error) {
      setGetAllIntegrations({ data: [] });
      return;
    }

    setGetAllIntegrations({
      ...resp,
      data: Array.isArray(resp?.data) ? resp.data : [],
    });
  }, [dispatch]);

  useEffect(() => {
    fetchIntegrationDetails();
  }, [fetchIntegrationDetails]);

  const getSelectedConnectors = (typed) => {
    return getAllConnectors?.find(
      (item) => item?.type?.toUpperCase() === typed,
    );
  };

  const handelAddGitDetailsClick = () => {
    setOpenAddGitIntegrations(true);
  };
  return (
    <Box
      sx={{
        margin: 0,
        padding: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#F5F5F5",
      }}
    >
      <Box
        sx={{
          padding: { xs: "8px 8px 0px 8px", md: "12px 12px 0px 12px" },
          flexShrink: 0,
        }}
      >
        <CommonBreadcrumbs
          items={[
            { label: "Settings" },
            { label: "Feature Portfolio" },
            { label: "Integration" },
          ]}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: { xs: "0 8px 8px 8px", md: "0 12px 12px 12px" },
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1,
            mt: 1,
          }}
        >
          <CustomFilterV2
            anchorEl={anchorElIntegrationFilter}
            open={openIntegrationFilter}
            widthPerColumn={"200px"}
            handleClick={handleIntegrationFilterIconClick}
            handleFilterButton={handleIntegrationFilterButton}
            handleClose={handleIntegrationFilterIconClose}
            sections={[
              {
                heading: "Sorting",
                content: (
                  <Box>
                    <Box
                      sx={{
                        pl: 3,
                        pt: 1.5,
                        pb: 1,
                      }}
                    >
                      <CustomRadioButtonV2
                        value={filters.sortBy}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            sortBy: e.target.value,
                          }))
                        }
                        data={[
                          { value: "Created at", label: "Created At" },
                          { value: "Updated at", label: "Updated At" },
                        ]}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        component="button"
                        type="button"
                        onClick={() =>
                          setFilters((prev) => ({
                            ...prev,
                            sortOrder: "Sort Asc –Des",
                          }))
                        }
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          cursor: "pointer",
                          px: 1.5,
                          py: 0.75,
                          borderRadius: 0,
                          width: "100%",
                          border: "none",
                          backgroundColor:
                            filters?.sortOrder === "Sort Asc –Des"
                              ? "#26890D"
                              : "transparent",
                          color:
                            filters?.sortOrder === "Sort Asc –Des"
                              ? "#FFFFFF"
                              : "#000000",
                        }}
                      >
                        <img
                          src={sortUp}
                          alt="Sort Asc–Des"
                          style={{
                            width: 16,
                            height: 16,
                          }}
                        />
                        <Typography variant="body2" sx={{ color: "inherit" }}>
                          Sort Asc –Des
                        </Typography>
                      </Box>

                      <Box
                        component="button"
                        type="button"
                        onClick={() =>
                          setFilters((prev) => ({
                            ...prev,
                            sortOrder: "Sort Des–Asc",
                          }))
                        }
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          cursor: "pointer",
                          px: 1.5,
                          py: 0.75,
                          borderRadius: 0,
                          width: "100%",
                          border: "none",
                          backgroundColor:
                            filters?.sortOrder === "Sort Des–Asc"
                              ? "#26890D"
                              : "transparent",
                          color:
                            filters?.sortOrder === "Sort Des–Asc"
                              ? "#FFFFFF"
                              : "#000000",
                        }}
                      >
                        <img
                          src={sortDown}
                          alt="Sort Des–Asc"
                          style={{
                            width: 16,
                            height: 16,
                          }}
                        />
                        <Typography variant="body2" sx={{ color: "inherit" }}>
                          Sort Des–Asc
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ),
              },
              {
                heading: "Filter by Connection",
                content: (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CustomCheckBoxV2
                      value={filters.connectionStatus.all}
                      onChange={() =>
                        setFilters((prev) => {
                          const newAllValue = !prev.connectionStatus.all;
                          return {
                            ...prev,
                            connectionStatus: {
                              all: newAllValue,
                              connected: newAllValue,
                              disconnected: newAllValue,
                            },
                          };
                        })
                      }
                      label="All"
                    />
                    <CustomCheckBoxV2
                      value={filters.connectionStatus.connected}
                      onChange={() =>
                        setFilters((prev) => {
                          const newConnected = !prev.connectionStatus.connected;
                          const newAll =
                            newConnected && prev.connectionStatus.disconnected;
                          return {
                            ...prev,
                            connectionStatus: {
                              ...prev.connectionStatus,
                              connected: newConnected,
                              all: newAll,
                            },
                          };
                        })
                      }
                      label="Connected"
                    />
                    <CustomCheckBoxV2
                      value={filters.connectionStatus.disconnected}
                      onChange={() =>
                        setFilters((prev) => {
                          const newDisconnected =
                            !prev.connectionStatus.disconnected;
                          const newAll =
                            newDisconnected && prev.connectionStatus.connected;
                          return {
                            ...prev,
                            connectionStatus: {
                              ...prev.connectionStatus,
                              disconnected: newDisconnected,
                              all: newAll,
                            },
                          };
                        })
                      }
                      label="Dis-Connected"
                    />
                  </Box>
                ),
              },
              {
                heading: "Filter by Page",
                content: (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CustomCheckBoxV2
                      value={filters.pages.all}
                      onChange={() =>
                        setFilters((prev) => {
                          const newAllValue = !prev.pages.all;
                          return {
                            ...prev,
                            pages: {
                              all: newAllValue,
                              testCases: newAllValue,
                              virtualLabCar: newAllValue,
                              legalRequirement: newAllValue,
                              projectConfigurator: newAllValue,
                            },
                          };
                        })
                      }
                      label="All"
                    />
                    <CustomCheckBoxV2
                      value={filters.pages.testCases}
                      onChange={() =>
                        setFilters((prev) => {
                          const newTestCases = !prev.pages.testCases;
                          const newAll =
                            newTestCases &&
                            prev.pages.virtualLabCar &&
                            prev.pages.legalRequirement &&
                            prev.pages.projectConfigurator;
                          return {
                            ...prev,
                            pages: {
                              ...prev.pages,
                              testCases: newTestCases,
                              all: newAll,
                            },
                          };
                        })
                      }
                      label="Test Cases"
                    />
                    <CustomCheckBoxV2
                      value={filters.pages.virtualLabCar}
                      onChange={() =>
                        setFilters((prev) => {
                          const newVirtualLabCar = !prev.pages.virtualLabCar;
                          const newAll =
                            newVirtualLabCar &&
                            prev.pages.testCases &&
                            prev.pages.legalRequirement &&
                            prev.pages.projectConfigurator;
                          return {
                            ...prev,
                            pages: {
                              ...prev.pages,
                              virtualLabCar: newVirtualLabCar,
                              all: newAll,
                            },
                          };
                        })
                      }
                      label="Virtual Lab-Car"
                    />
                    <CustomCheckBoxV2
                      value={filters.pages.legalRequirement}
                      onChange={() =>
                        setFilters((prev) => {
                          const newLegalRequirement =
                            !prev.pages.legalRequirement;
                          const newAll =
                            newLegalRequirement &&
                            prev.pages.testCases &&
                            prev.pages.virtualLabCar &&
                            prev.pages.projectConfigurator;
                          return {
                            ...prev,
                            pages: {
                              ...prev.pages,
                              legalRequirement: newLegalRequirement,
                              all: newAll,
                            },
                          };
                        })
                      }
                      label="Legal Requirement"
                    />
                    <CustomCheckBoxV2
                      value={filters.pages.projectConfigurator}
                      onChange={() =>
                        setFilters((prev) => {
                          const newProjectConfigurator =
                            !prev.pages.projectConfigurator;
                          const newAll =
                            newProjectConfigurator &&
                            prev.pages.testCases &&
                            prev.pages.virtualLabCar &&
                            prev.pages.legalRequirement;
                          return {
                            ...prev,
                            pages: {
                              ...prev.pages,
                              projectConfigurator: newProjectConfigurator,
                              all: newAll,
                            },
                          };
                        })
                      }
                      label="Project Configurator"
                    />
                  </Box>
                ),
              },
              {
                heading: "Filter by Tool",
                content: (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CustomCheckBoxV2
                      value={filters.toolTypes.all}
                      onChange={() =>
                        setFilters((prev) => {
                          const newAllValue = !prev.toolTypes.all;
                          return {
                            ...prev,
                            toolTypes: {
                              all: newAllValue,
                              jiraXray: newAllValue,
                              jiraSoftware: newAllValue,
                              snow: newAllValue,
                              github: newAllValue,
                              codeBeamer: newAllValue,
                            },
                          };
                        })
                      }
                      label="All"
                    />
                    <CustomCheckBoxV2
                      value={filters.toolTypes.jiraXray}
                      onChange={() =>
                        setFilters((prev) => {
                          const newJiraXray = !prev.toolTypes.jiraXray;
                          const newAll =
                            newJiraXray &&
                            prev.toolTypes.jiraSoftware &&
                            prev.toolTypes.snow &&
                            prev.toolTypes.github;
                          return {
                            ...prev,
                            toolTypes: {
                              ...prev.toolTypes,
                              jiraXray: newJiraXray,
                              all: newAll,
                            },
                          };
                        })
                      }
                      label="Jira Xray"
                    />
                    <CustomCheckBoxV2
                      value={filters.toolTypes.jiraSoftware}
                      onChange={() =>
                        setFilters((prev) => {
                          const newJiraSoftware = !prev.toolTypes.jiraSoftware;
                          const newAll =
                            newJiraSoftware &&
                            prev.toolTypes.jiraXray &&
                            prev.toolTypes.snow &&
                            prev.toolTypes.github;
                          return {
                            ...prev,
                            toolTypes: {
                              ...prev.toolTypes,
                              jiraSoftware: newJiraSoftware,
                              all: newAll,
                            },
                          };
                        })
                      }
                      label="Jira Software"
                    />
                    <CustomCheckBoxV2
                      value={filters.toolTypes.snow}
                      onChange={() =>
                        setFilters((prev) => {
                          const newSnow = !prev.toolTypes.snow;
                          const newAll =
                            newSnow &&
                            prev.toolTypes.jiraXray &&
                            prev.toolTypes.jiraSoftware &&
                            prev.toolTypes.github;
                          return {
                            ...prev,
                            toolTypes: {
                              ...prev.toolTypes,
                              snow: newSnow,
                              all: newAll,
                            },
                          };
                        })
                      }
                      label="ServiceNow"
                    />
                    <CustomCheckBoxV2
                      value={filters.toolTypes.github}
                      onChange={() =>
                        setFilters((prev) => {
                          const newGitHub = !prev.toolTypes.github;
                          const newAll =
                            newGitHub &&
                            prev.toolTypes.jiraXray &&
                            prev.toolTypes.jiraSoftware &&
                            prev.toolTypes.snow &&
                            prev.toolTypes.codeBeamer;
                          return {
                            ...prev,
                            toolTypes: {
                              ...prev.toolTypes,
                              github: newGitHub,
                              all: newAll,
                            },
                          };
                        })
                      }
                      label="GitHub"
                    />
                    <CustomCheckBoxV2
                      value={filters.toolTypes.codeBeamer}
                      onChange={() =>
                        setFilters((prev) => {
                          const newCodeBeamer = !prev.toolTypes.codeBeamer;
                          const newAll =
                            newCodeBeamer &&
                            prev.toolTypes.jiraXray &&
                            prev.toolTypes.jiraSoftware &&
                            prev.toolTypes.snow &&
                            prev.toolTypes.github;
                          return {
                            ...prev,
                            toolTypes: {
                              ...prev.toolTypes,
                              codeBeamer: newCodeBeamer,
                              all: newAll,
                            },
                          };
                        })
                      }
                      label="codeBeamer"
                    />
                  </Box>
                ),
              },
            ]}
          />
          <CustomSearchbarWithApiV2
            apiAction={integrationConfigAction.view_integrations}
            params={{ is_deleted: false }}
            searchKey={"connector_type"}
            getDataOnSearch={(data, resp) => {
              if (resp?.error) {
                setGetAllIntegrations({ data: [] });
                return;
              }

              setGetAllIntegrations({
                ...resp,
                data: Array.isArray(data) ? data : [],
              });
            }}
            placeholder={"Search"}
          />
          <Box>
            <AddButtonV2 handleOnClick={() => setOpenAddIntegrations(true)} />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          height: "70vh", // ✅ Takes 70% of screen height
          width: "100%",
          display: "flex",
          flexDirection: "column",
          mt: -1,
        }}
      >
        <Box
          sx={{
            p: 2,
            overflow: "auto",
            height: "100%",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#c1c1c1",
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: "#a8a8a8",
              },
            },
            scrollbarWidth: "thin",
            scrollbarColor: "#c1c1c1 #f1f1f1",
          }}
        >
          {integrationsList?.map((item) => {
            const integrateSelect = getSelectedConnectors(
              item?.connector_type?.toUpperCase(),
            );
            return (
              <Box
                key={item.id}
                sx={{
                  border: "1px solid #e0e0e0",
                  p: 1.5,
                  backgroundColor: "#fff",
                  position: "relative",
                  "&:hover": {
                    backgroundColor: "#fafafa",
                  },
                }}
              >
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      backgroundColor: getIconColor(integrateSelect?.name),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      overflow: "hidden",
                    }}
                  >
                    {getToolIcon(integrateSelect?.name) ? (
                      <img
                        src={getToolIcon(integrateSelect?.name)}
                        alt={integrateSelect?.name}
                        style={{
                          width: "32px",
                          height: "32px",
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = "block";
                          }
                        }}
                      />
                    ) : null}
                    <Typography
                      sx={{
                        color: "white",
                        fontWeight: 600,
                        fontSize: 16,
                        display: getToolIcon(integrateSelect?.name)
                          ? "none"
                          : "block",
                      }}
                    >
                      {integrateSelect?.name?.charAt(0) || "T"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "flex-start",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        fontStyle: "Open Sans",
                        mb: 0.25,
                        fontSize: "14px",
                        lineHeight: 1.2,
                      }}
                    >
                      Tool Name - {toSentenceCase(integrateSelect?.name)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        fontStyle: "Open Sans",
                        mb: 0.25,
                        fontSize: "14px",
                        lineHeight: 1.1,
                      }}
                    >
                      Page - {toTitleCase(item.module_type)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        fontStyle: "Open Sans",
                        mb: 0.5,
                        fontSize: "14px",
                        lineHeight: 1.1,
                      }}
                    >
                      Description -{" "}
                      {toSentenceCase(integrateSelect?.description || "-")}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color: "#999",
                        fontStyle: "Open Sans",
                        display: "block",
                        fontSize: "12px",
                        lineHeight: 1,
                      }}
                    >
                      Created At:{" "}
                      {new Date(
                        item.created_at || Date.now(),
                      ).toLocaleDateString()}
                      ,{" "}
                      {new Date(
                        item.created_at || Date.now(),
                      ).toLocaleTimeString()}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      justifyContent: "space-between",
                      height: "100%",
                      gap: 0.5,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(event) => handleMoreVertClick(event, item)}
                      sx={{ alignSelf: "flex-end" }}
                    >
                      <MoreVert />
                    </IconButton>
                    {item.is_connected ? (
                      <Typography
                        variant="caption"
                        sx={{
                          fontStyle: "Open Sans",
                          textAlign: "right",
                          fontSize: "12px",
                          lineHeight: 1,
                          color: "#007CB0",
                        }}
                      >
                        Connected
                      </Typography>
                    ) : (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#000000",
                          fontStyle: "Open Sans",
                          textAlign: "right",
                          fontSize: "12px",
                          lineHeight: 1,
                        }}
                      >
                        Disconnected
                      </Typography>
                    )}

                    <Typography
                      variant="caption"
                      sx={{
                        color: "#999",
                        fontStyle: "Open Sans",
                        textAlign: "right",
                        fontSize: "12px",
                        lineHeight: 1,
                      }}
                    >
                      Updated At:{" "}
                      {new Date(
                        item.updated_at || Date.now(),
                      ).toLocaleDateString()}
                      ,{" "}
                      {new Date(
                        item.updated_at || Date.now(),
                      ).toLocaleTimeString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}

          {integrationsList?.length === 0 && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="h6" sx={{ color: "#666", mb: 2 }}>
                No integrations found
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Menu
        anchorEl={anchorElMenu}
        open={Boolean(anchorElMenu)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {selectedItem?.connector_type?.toUpperCase().includes("GIT") &&
          selectedItem?.is_connected && (
            <MenuItem onClick={handelAddGitDetailsClick}>
              <ListItemText>Add Git Details</ListItemText>
              <ListItemIcon />
            </MenuItem>
          )}
        <MenuItem onClick={handleEditClick}>
          <ListItemText>Edit</ListItemText>
          <ListItemIcon />
        </MenuItem>
        <MenuItem onClick={handleDeleteMenuClick}>
          <ListItemText>Delete</ListItemText>
          <ListItemIcon />
        </MenuItem>
      </Menu>

      {openAddIntegrations && (
        <AddIntegrations
          open={openAddIntegrations}
          onClose={() => setOpenAddIntegrations(false)}
          fetchIntegrationDetails={fetchIntegrationDetails}
        />
      )}
      {openAddGitIntegrations && (
        <AddGitIntegrations
          open={openAddGitIntegrations}
          onClose={() => setOpenAddGitIntegrations(false)}
        />
      )}
      {openEditIntegrations && (
        <EditIntegrations
          open={openEditIntegrations}
          onClose={() => {
            setOpenEditIntegrations(false);
            setSelectedItem(null); // Clear selectedItem when dialog closes
          }}
          selectedItem={selectedItem}
          integrateSelect={getSelectedConnectors(
            selectedItem?.connector_type?.toUpperCase(),
          )}
          fetchIntegrationDetails={fetchIntegrationDetails}
        />
      )}
      {openDeleteIntegrations && (
        <CustomDialogBoxV2
          open={openDeleteIntegrations}
          onClose={() => setOpenDeleteIntegrations(false)}
          title="Delete Integration"
          successButtonName={"Yes"}
          rejectButtonName={"No"}
          onClickSuccess={handleConfirmDelete}
          onClickReject={() => setOpenDeleteIntegrations(false)}
          actionButtonStyle={"continuous"}
        >
          Are you sure you want to delete integration?
        </CustomDialogBoxV2>
      )}
    </Box>
  );
}

export default Integration;









